import { Landmark, Plus, Search } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { FirmFormDialog } from "@/features/firm-form";
import { useAuth } from "@/lib/auth";
import { FIRM_STATUSES, SYSTEMS_OF_RECORD, firmStatusText } from "@/lib/firms";
import { useFirms } from "@/lib/queries";
import { atLeast, type Firm } from "@/lib/types";

/**
 * Firms (ADR-045): the client's sheet as a table, with what the platform knows about each firm's
 * book. Supervisors read; admins create and edit. Nineteen rows today, so the filters are the
 * page's own — one request, narrowed here.
 */
export function FirmsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const firms = useFirms();
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("all");
  const [state, setState] = React.useState("all");
  const [creating, setCreating] = React.useState(false);

  const rows = firms.data ?? [];
  const states = [...new Set(rows.flatMap((f) => f.states))].sort();
  const q = search.trim().toLowerCase();
  const shown = rows.filter(
    (f) =>
      (status === "all" || f.status === status) &&
      (state === "all" || f.states.includes(state)) &&
      (!q || [f.name, f.label, f.code, ...f.tags.map((t) => t.tag)].some((s) => s.toLowerCase().includes(q))),
  );
  const filtered = Boolean(q) || status !== "all" || state !== "all";

  return (
    <Page>
      <PageHeader
        title="Firms"
        description={`${rows.length ? `${rows.length} firms · ` : ""}the clients whose leads the floor works · a lead's firm is on the lead, the calling campaign is shared`}
        action={
          canManage && (
            <Button onClick={() => setCreating(true)}>
              <Plus />
              New firm
            </Button>
          )
        }
      />
      <Card className="overflow-hidden">
        <Toolbar>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search firms"
            leading={<Search />}
            className="w-56"
            aria-label="Search firms"
          />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-36" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              {FIRM_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {firmStatusText(s).label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="w-32" aria-label="Filter by state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any state</SelectItem>
              {states.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filtered && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setStatus("all"); setState("all"); }}>
              Clear
            </Button>
          )}
        </Toolbar>
        {firms.isPending ? (
          <TableSkeleton cols={8} rows={6} />
        ) : firms.error ? (
          <ErrorState
            error={firms.error}
            title="Could not load the firms"
            action={
              <Button variant="outline" onClick={() => void firms.refetch()}>
                Try again
              </Button>
            }
          />
        ) : shown.length === 0 ? (
          <EmptyState
            icon={Landmark}
            title={filtered ? "No firms match" : "No firms yet"}
            description={filtered ? "Try another status or state." : "Seed the client's sheet (make seed-firms), or add one here."}
            action={canManage && !filtered ? <Button onClick={() => setCreating(true)}>New firm</Button> : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Firm</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>States</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Contacts</TableHead>
                  <TableHead className="text-right">Working</TableHead>
                  <TableHead className="text-right">Booked</TableHead>
                  <TableHead className="text-right">Signed this month</TableHead>
                  <TableHead>System of record</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((firm) => (
                  <FirmLine key={firm.id} firm={firm} onOpen={() => navigate(`/firms/${firm.id}`)} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      <FirmFormDialog open={creating} onOpenChange={setCreating} onCreated={(firm) => navigate(`/firms/${firm.id}`)} />
    </Page>
  );
}

function FirmLine({ firm, onOpen }: { firm: Firm; onOpen: () => void }) {
  const status = firmStatusText(firm.status);
  const record = typeof firm.settings.system_of_record === "string" ? firm.settings.system_of_record : "ghl";
  return (
    <TableRow
      interactive
      tabIndex={0}
      role="button"
      aria-label={`Open ${firm.label}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <TableCell className="whitespace-nowrap">
        <Link to={`/firms/${firm.id}`} className="font-medium text-text hover:underline" onClick={(e) => e.stopPropagation()}>
          {firm.label}
        </Link>
        {firm.name !== firm.label && <span className="block text-xs text-text-muted">{firm.name}</span>}
      </TableCell>
      <TableCell className="font-mono text-xs">{firm.code}</TableCell>
      <TableCell className="font-mono text-xs text-text-muted">{firm.tags.map((t) => t.tag).join(", ")}</TableCell>
      <TableCell className="text-xs">{firm.states.join(", ") || <span className="text-text-subtle">—</span>}</TableCell>
      <TableCell className="whitespace-nowrap">
        <Badge tone={status.tone} size="sm" title={firm.pause_reason ?? status.description}>
          {status.label}
        </Badge>
        {firm.counts.held > 0 && (
          <span className="ml-1.5 text-xs text-text-muted">{firm.counts.held.toLocaleString()} held</span>
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">{firm.counts.contacts.toLocaleString()}</TableCell>
      <TableCell className="text-right tabular-nums">{firm.counts.working.toLocaleString()}</TableCell>
      <TableCell className="text-right tabular-nums">{firm.counts.scheduled.toLocaleString()}</TableCell>
      <TableCell className="text-right tabular-nums">{firm.counts.signed_this_month.toLocaleString()}</TableCell>
      <TableCell className="text-xs text-text-muted">{SYSTEMS_OF_RECORD[record]?.label ?? record}</TableCell>
    </TableRow>
  );
}
